"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckSection,
  EncodeSection,
  SyndromeTable,
} from "@/components/tools/cs-tools/hamming-views";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  layoutForData,
  MAX_DATA_BITS,
  type Parity,
  parseBits,
} from "@/lib/tool-kit/cs-tools/hamming";
import {
  evaluateHamming,
  type HammingMode,
} from "@/lib/tool-kit/cs-tools/hamming-eval";

/** English strings. A translated content file overrides these through `ui`. */
export const HAMMING_CODE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modeEncode: "Encode data bits",
  modeCheck: "Check a received word",
  dataLabel: "Data bits",
  dataHint:
    "Up to 247 bits, leftmost first. Four bits give the classic Hamming (7,4) code; eleven give (15,11). Spaces are ignored.",
  dataDefault: "1011",
  receivedLabel: "Received codeword",
  receivedHint:
    "The word to check, leftmost bit first. With the overall parity bit switched on, the first bit is that overall bit (position 0).",
  receivedDefault: "0110111",
  flipLabel: "Flip bits to simulate noise (optional)",
  flipHint:
    "Positions to corrupt, such as 5 or 2, 6. Leave empty for a clean word. Valid positions run from {min} to {max}.",
  flipPlaceholder: "5",
  extendedLabel:
    "Add an overall parity bit (SECDED: correct one error, detect two)",
  parityLabel: "Parity convention",
  parityEven: "Even parity",
  parityOdd: "Odd parity",
  /** The bare word {parity} stands for inside parityBody and checksBody. */
  parityEvenWord: "even",
  parityOddWord: "odd",
  errEmptyData: "Enter the data bits to encode, such as 1011.",
  errInvalidData: "Data can contain only the digits 0 and 1.",
  errTooLongData:
    "That is more than 247 data bits, the largest size supported.",
  errEmptyReceived: "Enter the received codeword to check.",
  errInvalidReceived: "A codeword can contain only the digits 0 and 1.",
  errTooLongReceived: "That is longer than any supported Hamming codeword.",
  errLength:
    "That length is not a Hamming codeword. Valid lengths are 3, 5, 6, 7, 9 to 15, 17 to 31, 33 to 63 and so on — never a power of two. With the overall parity bit, add one.",
  errPositions: "Write positions as whole numbers separated by commas.",
  errOutOfRange: "One of those positions is outside the codeword.",
  resultsHeading: "Result",
  noiseHeading: "After the noise",
  receivedStat: "Received word",
  statCodeword: "Codeword",
  statCode: "Code (length, data bits)",
  statRate: "Code rate (data ÷ total)",
  codeName: "({n}, {k})",
  statSyndrome: "Syndrome",
  statCorrected: "Corrected word",
  statData: "Data recovered",
  copyCodeword: "Copy codeword",
  copyCorrected: "Copy corrected word",
  copied: "Copied!",
  gridCaption: "The codeword, one bit per position",
  gridCorner: "Row",
  rowRole: "Role",
  rowSent: "Sent",
  rowReceived: "Received",
  rowCorrected: "Corrected",
  roleOverall: "P0 (overall)",
  parityHeading: "How each parity bit is chosen",
  parityBody:
    "Each parity bit sits at a power-of-two position and covers every position whose binary index contains that power. It is set so the covered group has {parity} parity.",
  parityCaption: "Parity bit calculations",
  colCheck: "Parity bit",
  colCovers: "Positions covered",
  colDataBits: "Other bits covered",
  colOnes: "Ones counted",
  colParityBit: "Parity bit value",
  colReceivedBits: "Received bits",
  colResult: "Result",
  colMeaning: "Meaning",
  colSyndromeBinary: "Syndrome (binary)",
  colSyndromeValue: "Syndrome (decimal)",
  overallNote:
    "The overall parity bit at position 0 is {bit}, chosen so the whole word, overall bit included, has the same parity as the checks.",
  checksHeading: "The parity checks",
  checksBody:
    "Each check counts the ones in its group, including its own parity bit. A group that should have {parity} parity but does not has failed. The failed checks, read as a binary number with P1 as the lowest digit, form the syndrome.",
  checksCaption: "Parity checks on the received word",
  resultPass: "Passed",
  resultFail: "Failed",
  overallHolds: "The overall parity check passed.",
  overallFails:
    "The overall parity check failed: an odd number of bits changed.",
  verdictNone: "No error detected. Every parity check passed.",
  verdictOverall:
    "Every Hamming check passed but the overall parity failed, so the error is in the overall parity bit itself. The data is intact.",
  verdictDouble:
    "Two errors detected. The syndrome is {syndrome}, but the overall parity still holds, which a single error cannot cause. The word cannot be corrected — request it again.",
  verdictInvalid:
    "The syndrome {syndrome} points past the end of this {n}-bit word, so more than one bit is wrong. The word cannot be corrected.",
  verdictCorrected:
    "One error found at position {position} ({role}). Flipping that bit back repairs the word.",
  miscorrectNote:
    "The corrected word does not match the word that was sent. More bits were flipped than this code can repair, so it fixed the wrong one — add the overall parity bit to detect double errors.",
  syndromeHeading: "Syndrome table",
  syndromeBody:
    "The syndrome is the position of a single flipped bit, so this table is the whole decoder.",
  syndromeCaption: "What each syndrome value means",
  syndromeNone: "No error",
  syndromeAt: "Error at position {position} ({role})",
  syndromeImpossible:
    "Cannot occur from one error: the word has only {n} positions",
  syndromeLimited: "Showing the first {shown} of {total} syndrome values.",
} as const;

type Strings = typeof HAMMING_CODE_CALCULATOR_STRINGS;

const PLAIN: Intl.NumberFormatOptions = {
  useGrouping: false,
  maximumFractionDigits: 0,
};

export default function HammingCodeCalculatorWidget() {
  const strings = useToolUi(HAMMING_CODE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<HammingMode>("encode");
  const [extended, setExtended] = useState(false);
  const [parity, setParity] = useState<Parity>("even");
  const [dataText, setDataText] = useState<string>(strings.dataDefault);
  const [flipText, setFlipText] = useState("");
  const [receivedText, setReceivedText] = useState<string>(
    strings.receivedDefault
  );

  const view = evaluateHamming({
    mode,
    options: { extended, parity },
    dataText,
    flipText,
    receivedText,
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <PillGroup
          label={strings.modeLabel}
          name="hamming-mode"
          onChange={setMode}
          options={[
            { value: "encode", label: strings.modeEncode },
            { value: "check", label: strings.modeCheck },
          ]}
          value={mode}
        />
        <PillGroup
          label={strings.parityLabel}
          name="hamming-parity"
          onChange={setParity}
          options={[
            { value: "even", label: strings.parityEven },
            { value: "odd", label: strings.parityOdd },
          ]}
          value={parity}
        />
      </div>
      <Checkbox
        checked={extended}
        label={strings.extendedLabel}
        onChange={setExtended}
      />
      {mode === "encode" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            hint={strings.dataHint}
            id="hamming-data"
            label={strings.dataLabel}
            mono
            onChange={setDataText}
            value={dataText}
          />
          <TextField
            hint={flipHint(strings, dataText, extended, fmt)}
            id="hamming-flip"
            label={strings.flipLabel}
            mono
            onChange={setFlipText}
            placeholder={strings.flipPlaceholder}
            value={flipText}
          />
        </div>
      ) : (
        <TextField
          hint={strings.receivedHint}
          id="hamming-received"
          label={strings.receivedLabel}
          mono
          onChange={setReceivedText}
          value={receivedText}
        />
      )}

      <div aria-live="polite" className="space-y-5">
        {view.kind === "error" ? (
          <ErrorNotice>{strings[view.key]}</ErrorNotice>
        ) : null}
        {view.kind === "encode" ? (
          <>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            <EncodeSection encoded={view.encoded} fmt={fmt} strings={strings} />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyCodeword}
              text={view.encoded.codeword}
            />
            {view.check && view.received ? (
              <>
                <h3 className={WIDGET_H3}>{strings.noiseHeading}</h3>
                <CheckSection
                  check={view.check}
                  fmt={fmt}
                  sent={view.encoded.codeword}
                  strings={strings}
                />
              </>
            ) : null}
            <SyndromeTable
              extended={extended}
              fmt={fmt}
              layout={view.encoded.layout}
              strings={strings}
            />
          </>
        ) : null}
        {view.kind === "check" ? (
          <>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            <CheckSection
              check={view.check}
              fmt={fmt}
              sent={null}
              strings={strings}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyCorrected}
              text={view.check.corrected}
            />
            <SyndromeTable
              extended={extended}
              fmt={fmt}
              layout={view.check.layout}
              strings={strings}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

function flipHint(
  strings: Strings,
  dataText: string,
  extended: boolean,
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string
): string {
  const parsed = parseBits(dataText, MAX_DATA_BITS);
  const max = parsed.ok ? layoutForData(parsed.bits.length).n : 7;
  return t(strings.flipHint, {
    min: fmt(extended ? 0 : 1, PLAIN),
    max: fmt(max, PLAIN),
  });
}
